/**
 * Sewing Efficiency — the filter declaration, and the two pieces of URL state that are not
 * filters (CLAUDE.md §4.4).
 *
 * Mirrors the `filters:` block of `winsight-server`'s `reports/sewing-efficiency/report.yaml`,
 * which is authoritative: `from`/`to` and `factory` are required, `building`/`floor`/`line`/
 * `brand` are multi-valued (comma-joined in the URL, `IN (:name)` on the wire), and `include_ppa`
 * is a boolean the server coerces from `true`/`false`.
 *
 * `view` travels in the request envelope rather than in `filters`, but is still answer-defining, so
 * it round-trips through the URL and is validated with zod here.
 *
 * **The schema is built per factory** because building, floor and line codes collide across
 * factories — three of them have a floor called `1` — so one flat list would accept a shared link
 * pairing BCA with a BVN floor. The lists are a snapshot of
 * `managementportal.baseportal_sys_office_lineconvert`, filtered as the queries filter it, until a
 * dictionary endpoint exists. See `design-notes.md`.
 */

import { format, subDays } from 'date-fns';
import { z } from 'zod';

import { FACTORY_OPTIONS, isFactoryCode, resolveFactory, type FactoryCode } from '@/lib/factories';
import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

const ISO = 'yyyy-MM-dd';

/** A week ending yesterday: today is still being recorded. No legacy default to reproduce. */
const DEFAULT_WINDOW_DAYS = 7;

/** Resolved on every parse, never frozen at module load: a tab left open past midnight must move. */
export function defaultPeriod(today: Date = new Date()): { from: string; to: string } {
  const end = subDays(today, 1);
  return {
    from: format(subDays(end, DEFAULT_WINDOW_DAYS - 1), ISO),
    to: format(end, ISO),
  };
}

/** Factory codes, after the legacy PG/PGA/PGV/PGN → PGV collapse. Re-exported from `lib`. */
export {
  FACTORY_OPTIONS,
  isFactoryCode,
  resolveFactory,
  type FactoryCode,
} from '@/lib/factories';

/**
 * The factories this report covers. BVG and BYS are withdrawn — the strip plan holds no rows for
 * either, and their only other target source is fabricated. The server withdraws them too, so a
 * hand-edited URL returns empty rather than fake targets. Deliberately not a change to
 * `@/lib/factories`, which Sewing Floor Board shares and which is unaffected.
 */
export const SEWING_FACTORY_OPTIONS: readonly FilterOption[] = FACTORY_OPTIONS.filter(
  (option) => option.value !== 'BVG' && option.value !== 'BYS',
);

/** Like the shared `resolveFactory`, but never lands on a withdrawn factory. */
export function resolveSewingFactory(
  fromUrl: string | null,
  loginFactory: string | null | undefined,
): FactoryCode {
  const resolved = resolveFactory(fromUrl, loginFactory);
  if (SEWING_FACTORY_OPTIONS.some((option) => option.value === resolved)) {
    return resolved;
  }
  // No bang: TypeScript cannot prove a `.filter()` result is non-empty, and the next withdrawal
  // could leave it holding nothing.
  const first = SEWING_FACTORY_OPTIONS[0]?.value;
  return isFactoryCode(first) ? first : 'BCA';
}

/** Live options — §5.4. Never a hardcoded array here; `option-sets.yaml` owns the shared lists. */
const live = liveOptionsFor('sewing-efficiency');

interface FactorySetup {
  buildings: readonly string[];
  floors: readonly string[];
  /** `sfts_line` code and the line's name, as `code=label`. Kept packed to stay reviewable. */
  lines: readonly string[];
}

/** Snapshot of the sewing lines per factory — see the header note for provenance. */
const FACTORY_SETUP: Record<FactoryCode, FactorySetup> = {
  BCA: {
    buildings: ['A', 'B', 'D', 'PPA'],
    floors: ['1', '2', '3', '4', '5', 'F', 'Sample Room'],
    lines: [
      'SLBCAAXF101=A01', 'SLBCAAXF102=A02', 'SLBCAAXF103=A03', 'SLBCAAXF104=A04',
      'SLBCAAXF105=A05', 'SLBCAAXF106=A06', 'SLBCAAXF107=A07', 'SLBCAAXF108=A08',
      'SLBCAAXF109=A09', 'SLBCAAXF110=A10', 'SLBCAAXF111=A11', 'SLBCAAXF112=A112',
      'SLBCAAXF214=A14', 'SLBCAAXF215=A15', 'SLBCAAXF216=A16', 'SLBCAAXF163=A163',
      'SLBCAAXF164=A164', 'SLBCAAXF165=A165', 'SLBCAAXF217=A17', 'SLBCAAXF218=A18',
      'SLBCAAXF219=A19', 'SLBCAAXF220=A20', 'SLBCAAXF221=A21', 'SLBCAAXF211=A211',
      'SLBCAAXF212=A212', 'SLBCAAXF213=A213', 'SLBCAAXF222=A22', 'SLBCAAXF225=A225',
      'SLBCAAXF223=A23', 'SLBCAAXF224=A24', 'SLBCAAXF260=A260', 'SLBCAAXF261=A261',
      'SLBCAAXF262=A262', 'SLBCAAXF321=A321', 'SLBCAAXF322=A322', 'SLBCAAXF323=A323',
      'SLBCAAXF324=A324', 'SLBCAAXF325=A325', 'SLBCAAXF326=A326', 'SLBCAAXF336=A336',
      'SLBCAAXF357=A357', 'SLBCAAXF358=A358', 'SLBCAAXF359=A359', 'SLBCAAXF440=A40',
      'SLBCAAXF441=A41', 'SLBCAAXF442=A42', 'SLBCAAXF443=A43', 'SLBCAAXF431=A431',
      'SLBCAAXF432=A432', 'SLBCAAXF433=A433', 'SLBCAAXF434=A434', 'SLBCAAXF435=A435',
      'SLBCAAXF436=A436', 'SLBCAAXF437=A437', 'SLBCAAXF438=A438', 'SLBCAAXF439=A439',
      'SLBCAAXF444=A44', 'SLBCAAXF445=A45', 'SLBCAAXF452=A452', 'SLBCAAXF454=A454',
      'SLBCAAXF455=A455', 'SLBCAAXF456=A456', 'SLBCAAXF446=A46', 'SLBCAAXF447=A47',
      'SLBCAAXF448=A48', 'SLBCAAXF449=A49', 'SLBCAAXF450=A50', 'SLBCAAXF553=A53',
      'SLBCAAXF554=A54', 'SLBCAAXF541=A541', 'SLBCAAXF542=A542', 'SLBCAAXF543=A543',
      'SLBCAAXF544=A544', 'SLBCAAXF545=A545', 'SLBCAAXF546=A546', 'SLBCAAXF547=A547',
      'SLBCAAXF548=A548', 'SLBCAAXF549=A549', 'SLBCAAXF555=A55', 'SLBCAAXF550=A550',
      'SLBCAAXF551=A551', 'SLBCAAXF552=A552', 'SLBCAAXF556=A56', 'SLBCAAXF565=A565',
      'SLBCAAXF557=A57', 'SLBCAAXF558=A58', 'SLBCAAXF559=A59', 'SLBCAAXF560=A60',
      'SLBCAAXF561=A61', 'SLBCAAXF562=A62', 'SLBCAAXF563=A63', 'SLBCABXF101=B01',
      'SLBCABXF102=B02', 'SLBCABXF103=B03', 'SLBCABXF104=B04', 'SLBCABXF105=B05',
      'SLBCABXF106=B06', 'SLBCABXF107=B07', 'SLBCABXF108=B08', 'SLBCABXF109=B09',
      'SLBCABXF110=B10', 'SLBCABXF111=B11', 'SLBCABXF112=B112', 'SLBCABXF214=B14',
      'SLBCABXF215=B15', 'SLBCABXF216=B16', 'SLBCABXF217=B17', 'SLBCABXF218=B18',
      'SLBCABXF219=B19', 'SLBCABXF220=B20', 'SLBCABXF221=B21', 'SLBCABXF222=B22',
      'SLBCABXF225=B225', 'SLBCABXF223=B23', 'SLBCABXF224=B24', 'SLBCABXF330=B30',
      'SLBCABXF331=B31', 'SLBCABXF333=B33', 'SLBCABXF339=B339', 'SLBCABXF354=B354',
      'SLBCABXF441=B441', 'SLBCABXF442=B442', 'SLBCABXF443=B443', 'SLBCABXF445=B445',
      'SLBCABXF446=B446', 'SLBCABXF447=B447', 'SLBCABXF448=B48', 'SLBCABXF449=B49',
      'SLBCABXF450=B50', 'SLBCABXF452=B52', 'SLBCABXF453=B53', 'SLBCABXF454=B54',
      'SLBCADXF101=D01', 'SLBCADXF102=D02', 'SLBCADXF103=D03', 'SLBCADXF104=D04',
      'SLBCAPPA02=PPA02', 'SLBCAPPA04=PPA04', 'SLBCAPPA05=PPA05', 'SLBCAPPA08=PPA08',
      'SLBCABXFPPA09=PPA09 (SLBCABXFPPA09)', 'SLBCAPPA09=PPA09 (SLBCAPPA09)', 'SLBCAPPA10=PPA10',
      'SLBCAPPA11=PPA11', 'SLBCAPPA1=PPA110', 'Sample Line 001=Sample Line 001',
    ],
  },
  BVG: {
    buildings: ['B', 'C', 'D', 'E', 'H', 'LR', 'S'],
    floors: ['3', '4', '5', 'F'],
    lines: [
      'SLBVGHXF201=201', 'SLBVGSXF111=206', 'SLBVGBX3001=B-3-001', 'SLBVGBX3002=B-3-002',
      'SLBVGBX3003=B-3-003', 'SLBVGBX3005=B-3-005', 'SLBVGBX3006=B-3-006', 'SLBVGBX3007=B-3-007',
      'SLBVGBX3008=B-3-008', 'SLBVGBX4011=B-4-011', 'SLBVGBX4013=B-4-013', 'SLBVGBX4015=B-4-015',
      'SLBVGBX4016=B-4-016', 'SLBVGBX4017=B-4-017', 'SLBVGBX4018=B-4-018', 'SLBVGBX4012=BX 012',
      'SLBVGCX3021=C-3-021', 'SLBVGCX3022=C-3-022', 'SLBVGCX3023=C-3-023', 'SLBVGCX3025=C-3-025',
      'SLBVGCX3026=C-3-026', 'SLBVGCX3027=C-3-027', 'SLBVGCX3028=C-3-028 (SLBVGCX3028)',
      'SLBVGCX3029=C-3-029', 'SLBVGCX4031=C-4-031', 'SLBVGCX4032=C-4-032', 'SLBVGCX4033=C-4-033',
      'SLBVGCX4035=C-4-035', 'SLBVGCX4036=C-4-036', 'SLBVGCX4037=C-4-037', 'SLBVGCX4038=C-4-038',
      'SLBVGCX4039=C-4-039', 'SLBVGCX5045=C-5-045', 'SLBVGCX5046=C-5-046', 'SLBVGCX5047=C-5-047',
      'SLBVGCX5043=C精简43组', 'SLBVGCX5048=C精简48组', 'SLBVGDX3007=D-3-007', 'SLBVGDX3051=D-3-051',
      'SLBVGDX3052=D-3-052 (SLBVGDX3052)', 'SLBVGDX3053=D-3-053 (SLBVGDX3053)',
      'SLBVGDX3055=D-3-055', 'SLBVGDX3056=D-3-056 (SLBVGDX3056)', 'SLBVGDX3057=D-3-057',
      'SLBVGDX3058=D-3-058', 'SLBVGDX3059=D-3-059', 'SLBVGDX4061=D-4-061', 'SLBVGDX4062=D-4-062',
      'SLBVGDX4063=D-4-063', 'SLBVGDX4065=D-4-065', 'SLBVGDX4066=D-4-066', 'SLBVGDX4067=D-4-067',
      'SLBVGDX4068=D-4-068', 'SLBVGDX4069=D-4-069', 'SLBVGDX5071=D-5-071', 'SLBVGDX5072=D-5-072',
      'SLBVGDX5073=D-5-073', 'SLBVGDX5075=D-5-075', 'SLBVGDX5076=D-5-076', 'SLBVGDX5077=D-5-077',
      'SLBVGDX5078=D-5-078', 'SLBVGDX5079=DX 079', 'SLBVGEX3008=E-3-008', 'SLBVGEX3089=E-3-089',
      'SLBVGEX4001=E-4-001', 'SLBVGEX4005=E-4-005', 'SLBVGEX4081=E-4-081', 'SLBVGEX4082=E-4-082',
      'SLBVGEX4083=E-4-083', 'SLBVGEX4085=E-4-085', 'SLBVGEX4086=E-4-086', 'SLBVGEX4087=E-4-087',
      'SLBVGEX4088=E-4-088', 'SLBVGEX4091=E-4-091', 'SLBVGEX4092=E-4-092', 'SLBVGEX4093=E-4-093',
      'SLBVGEX4095=E-4-095', 'SLBVGEX4096=E-4-096', 'SLBVGEX4097=E-4-097', 'SLBVGEX4098=E-4-098',
      'SLBVGEX4099=E-4-099', 'SLBVGEX5101=E-5-101', 'SLBVGEX5102=E-5-102', 'SLBVGEX5103=E-5-103',
      'SLBVGEX5105=E-5-105', 'SLBVGEX5106=E-5-106', 'SLBVGEX5108=E-5-108', 'SLBVGEX5109=E-5-109',
      'SLBVGEX4012=EX 012', 'SLBVGHXF202=HX 202', 'SLBVGHXF203=HX 203', 'SLBVGHXF204=HX 204',
      'SLBVGHXF205=HX 205', 'SLBVGCX5011=LR11组', 'SLBVGLR3001=LR1组', 'SLBVGLR3002=LR2组',
      'SLBVGLR3003=LR3组', 'SLBVGLR3005=LR5组', 'SLBVGLR3006=LR6组', 'SLBVGLR3007=LR7组',
      'SLBVGLR5008=LR8组', 'SLBVGLR5009=LR9组', 'SLBVGMI4011=M-4-011', 'SLBVGMI4012=M-4-012',
      'SLBVGSXF112=S-F-112', 'SLBVGSXF113=SX113', 'SLBVGEX4002=精简2组', 'SLBVGEX4003=精简3组',
    ],
  },
  BVN: {
    buildings: ['K3', 'K4', 'K5'],
    floors: ['K3-F1', 'K4-F2', 'K4-F3', 'K5-F2', 'K5-F3', 'SUBCON'],
    lines: [
      'BVN.Jumper.K4.F2=BVN.Jumper.K4.F2', 'BVN.Jumper.K4.F3=BVN.Jumper.K4.F3',
      'BVN.Jumper.K5.F2=BVN.Jumper.K5.F2', 'BVN.Jumper.K5.F3=BVN.Jumper.K5.F3',
      'BVN.Others.K4.F2=BVN.Others.K4.F2', 'BVN.Others.K4.F3=BVN.Others.K4.F3',
      'BVN.Others.K5.F2=BVN.Others.K5.F2', 'BVN.Others.K5.F3=BVN.Others.K5.F3',
      'BVN.WATER SPIDER.K4.F2=BVN.WATER SPIDER K4.F2',
      'BVN.WATER SPIDER.K4.F3=BVN.WATER SPIDER K4.F3',
      'BVN.WATER SPIDER.K5.F2=BVN.WATER SPIDER K5.F2',
      'BVN.WATER SPIDER.K5.F3=BVN.WATER SPIDER K5.F3', 'SLBVNSUBCONHTG=HTG',
      'SLBVNK3F001=K3-F-01', 'SLBVNK3F002=K3-F-02', 'SLBVNK3F003=K3-F-03', 'SLBVNK3F004=K3-F-04',
      'SLBVNK3F005=K3-F-05', 'SLBVNK3F006=K3-F-06', 'SLBVNK3F007=K3-F-07', 'SLBVNK3F008=K3-F-08',
      'SLBVNK3F009=K3-F-09', 'SLBVNK3F010=K3-F-10', 'SLBVNK3F011=K3-F-11',
      'BVN.Jumper.K3.F=K3-F-12 (BVN.Jumper.K3.F)', 'SLBVNK3F012=K3-F-12 (SLBVNK3F012)',
      'SLBVNK3F013=K3-F-13', 'SLBVNK3F014=K3-F-14', 'SLBVNK3F015=K3-F-15', 'SLBVNK3F016=K3-F-16',
      'SLBVNK3F017=K3-F-17', 'SLBVNK3F018=K3-F-18', 'SLBVNK3F019=K3-F-19', 'SLBVNK3F020=K3-F-20',
      'SLBVNK42001=K4-02-01', 'SLBVNK42002=K4-02-02', 'SLBVNK42003=K4-02-03',
      'SLBVNK42004=K4-02-04', 'SLBVNK42005=K4-02-05', 'SLBVNK42006=K4-02-06',
      'SLBVNK42007=K4-02-07', 'SLBVNK42008=K4-02-08', 'SLBVNK42009=K4-02-09',
      'SLBVNK42010=K4-02-10', 'SLBVNK42011=K4-02-11', 'SLBVNK42012=K4-02-12',
      'SLBVNK42013=K4-02-13', 'SLBVNK42014=K4-02-14', 'SLBVNK42015=K4-02-15',
      'SLBVNK42016=K4-02-16', 'SLBVNK42017=K4-02-17', 'SLBVNK42018=K4-02-18',
      'SLBVNK42019=K4-02-19', 'SLBVNK42020=K4-02-20', 'SLBVNK42021=K4-02-21',
      'SLBVNK42022=K4-02-22', 'SLBVNK42023=K4-02-23', 'SLBVNK42024=K4-02-24',
      'SLBVNK42025=K4-02-25', 'SLBVNK42026=K4-02-26', 'SLBVNK42027=K4-02-27',
      'SLBVNK42028=K4-02-28', 'SLBVNK42029=K4-02-29', 'SLBVNK42030=K4-02-30',
      'SLBVNK43001=K4-03-01', 'SLBVNK43002=K4-03-02', 'SLBVNK43003=K4-03-03',
      'SLBVNK43004=K4-03-04', 'SLBVNK43005=K4-03-05', 'SLBVNK43006=K4-03-06',
      'SLBVNK43007=K4-03-07', 'SLBVNK43008=K4-03-08', 'SLBVNK43009=K4-03-09',
      'SLBVNK43010=K4-03-10', 'SLBVNK43011=K4-03-11', 'SLBVNK43012=K4-03-12',
      'SLBVNK43013=K4-03-13', 'SLBVNK43014=K4-03-14', 'SLBVNK43015=K4-03-15',
      'SLBVNK43016=K4-03-16', 'SLBVNK43017=K4-03-17', 'SLBVNK43018=K4-03-18',
      'SLBVNK43019=K4-03-19', 'SLBVNK43020=K4-03-20', 'SLBVNK43021=K4-03-21',
      'SLBVNK43022=K4-03-22', 'SLBVNK43023=K4-03-23', 'SLBVNK43024=K4-03-24',
      'SLBVNK43025=K4-03-25', 'SLBVNK43026=K4-03-26', 'SLBVNK43027=K4-03-27',
      'SLBVNK43028=K4-03-28', 'SLBVNK43029=K4-03-29', 'SLBVNK43030=K4-03-30',
      'SLBVNK43031=K4-03-31', 'SLBVNK43032=K4-03-32', 'SLBVNK43033=K4-03-33',
      'SLBVNK43034=K4-03-34', 'SLBVNK43035=K4-03-35', 'PPA.K4F3.01=K4-03-PPA1',
      'PPA.K4F3.02=K4-03-PPA2', 'SLBVNK52001=K5-02-01', 'SLBVNK52002=K5-02-02',
      'SLBVNK52003=K5-02-03', 'SLBVNK52004=K5-02-04', 'SLBVNK52005=K5-02-05',
      'SLBVNK52006=K5-02-06', 'SLBVNK52007=K5-02-07', 'SLBVNK52008=K5-02-08',
      'SLBVNK52009=K5-02-09', 'SLBVNK52010=K5-02-10', 'SLBVNK52011=K5-02-11',
      'SLBVNK52012=K5-02-12', 'SLBVNK52013=K5-02-13', 'SLBVNK52014=K5-02-14',
      'SLBVNK52015=K5-02-15', 'SLBVNK52016=K5-02-16', 'SLBVNK52017=K5-02-17',
      'SLBVNK52018=K5-02-18', 'SLBVNK52019=K5-02-19', 'SLBVNK52020=K5-02-20',
      'SLBVNK52021=K5-02-21', 'SLBVNK52022=K5-02-22', 'SLBVNK52023=K5-02-23',
      'SLBVNK52024=K5-02-24', 'SLBVNK52025=K5-02-25', 'SLBVNK52026=K5-02-26',
      'SLBVNK52027=K5-02-27', 'SLBVNK52028=K5-02-28', 'SLBVNK52029=K5-02-29',
      'SLBVNK53001=K5-03-01', 'SLBVNK53002=K5-03-02', 'SLBVNK53003=K5-03-03',
      'SLBVNK53004=K5-03-04', 'SLBVNK53005=K5-03-05', 'SLBVNK53006=K5-03-06',
      'SLBVNK53007=K5-03-07', 'SLBVNK53008=K5-03-08', 'SLBVNK53009=K5-03-09',
      'SLBVNK53010=K5-03-10', 'SLBVNK53011=K5-03-11', 'SLBVNK53012=K5-03-12',
      'SLBVNK53013=K5-03-13', 'SLBVNK53014=K5-03-14', 'SLBVNK53015=K5-03-15',
      'SLBVNK53016=K5-03-16', 'SLBVNK53017=K5-03-17', 'SLBVNK53018=K5-03-18',
      'SLBVNK53019=K5-03-19', 'SLBVNK53020=K5-03-20', 'SLBVNK53021=K5-03-21',
      'SLBVNK53024=K5-03-24', 'SLBVNK53025=K5-03-25', 'SLBVNK53026=K5-03-26',
      'SLBVNK53027=K5-03-27', 'SLBVNK53028=K5-03-28', 'PPA.K5F3.01=K5-03-PPA1',
      'PPA.K5F3.02=K5-03-PPA2', 'SLBVNK3PACK=Packing', 'Sample.G01=Sample.G01',
      'Sample.G02=Sample.G02', 'Sample.G04=Sample.G04', 'Sample.G05=Sample.G05',
      'Sample.G06=Sample.G06', 'Sample.G07=Sample.G07', 'Sample.G08=Sample.G08',
    ],
  },
  BYS: {
    buildings: ['1'],
    floors: ['01', '02', '03'],
    lines: [
      'SLBYSXXF001=001', 'SLBYSXXF002=002', 'SLBYSXXF003=003', 'SLBYSXXF004=004',
      'SLBYSXXF005=005', 'SLBYSXXF006=006', 'SLBYSXXF007=007', 'SLBYSXXF008=008',
      'SLBYSXXF009=009', 'SLBYSXXF010=010', 'SLBYSXXF011=011', 'SLBYSXXF012=012',
      'SLBYSXXF013=013', 'SLBYSXXF014=014', 'SLBYSXXF015=015', 'SLBYSXXF016=016',
      'SLBYSXXF017=017', 'SLBYSXXF018=018', 'SLBYSXXF019=019', 'SLBYSXXF020=020',
      'SLBYSXXF021=021', 'SLBYSXXF022=022', 'SLBYSXXF023=023', 'SLBYSXXF024=024',
      'SLBYSXXF025=025', 'SLBYSXXF026=026', 'SLBYSXXF027=027', 'SLBYSXXF028=028',
      'SLBYSXXF029=029', 'SLBYSXXF030=030', 'SLBYSXXF031=031', 'SLBYSXXF032=032',
      'SLBYSXXF033=033', 'SLBYSXXF034=034', 'SLBYSXXF035=035',
    ],
  },
  PGV: {
    buildings: ['P2 Sewing', 'P3 Sewing'],
    floors: ['1', '2'],
    lines: [
      'SLPGVP2A033=Adidas P2 Line 33', 'SLPGVP2A034=Adidas P2 Line 34',
      'SLPGVP2A035=Adidas P2 Line 35', 'SLPGVP2A036=Adidas P2 Line 36',
      'SLPGVP2A045=Adidas P2 Line 45', 'SLPGVP2A046=Adidas P2 Line 46',
      'SLPGVP2A051=Adidas P2 Line 51', 'SLPGVP3A037=Adidas P3 Line 37',
      'SLPGVP3A040=Adidas P3 Line 40', 'SLPGVP3A042=Adidas P3 Line 42',
      'SLPGVP3A044=Adidas P3 Line 44', 'PGV.PRD.SEW.MULTI.L02=Multi Line 02',
      'PGV.PRD.SEW.MULTI.L03=Multi Line 03', 'SLPGVP32001=N01', 'SLPGVP32002=N02',
      'SLPGVP32003=N03', 'SLPGVP32004=N04', 'SLPGVP32005=N05', 'SLPGVP32006=N06',
      'SLPGVP32007=N07', 'SLPGVP32008=N08', 'SLPGVP32009=N09', 'SLPGVP32010=N10',
      'SLPGVP32011=N11', 'SLPGVP32012=N12', 'SLPGVP32013=N13', 'SLPGVP32014=N14',
      'SLPGVP32015=N15', 'SLPGVP32016=N16', 'SLPGVP32017=N17', 'SLPGVP32018=N18',
      'SLPGVP32019=N19', 'SLPGVP32020=N20', 'SLPGVP32021=N21', 'SLPGVP32022=N22',
      'SLPGVP32023=N23', 'SLPGVP32024=N24', 'SLPGVP32025=N25', 'SLPGVP32026=N26',
      'PGV.PRD.SEW.SEW.AUTO01=P2-PPA-01', 'PGV.PRD.SEW.SEW.AUTO02=P2-PPA-02',
      'PGV.PRD.SEW.SEW.AUTO03=P2-PPA-03', 'PGV.PRD.SEW.NK.SEW.AUTO=P3-PPA-01',
      'SLPGVP2A048=SLPGVP2A048', 'PGV.AO.HR.SWC=Sewing Center', 'SLPGVP21001=V01',
      'SLPGVP24002=V02', 'SLPGVP21003=V03', 'SLPGVP21004=V04', 'SLPGVP21005=V05',
      'SLPGVP21007=V07', 'SLPGVP21008=V08', 'SLPGVP21009=V09', 'SLPGVP21011=V11',
      'SLPGVP21012=V12', 'SLPGVP21013=V13', 'SLPGVP21014=V14', 'SLPGVP21016=V16',
      'SLPGVP21017=V17', 'SLPGVP21019=V19', 'SLPGVP21020=V20', 'SLPGVP21021=V21',
      'SLPGVP21022=V22', 'SLPGVP21023=V23', 'SLPGVP21025=V25', 'SLPGVP21026=V26',
      'SLPGVP21027=V27', 'SLPGVP21028=V28', 'SLPGVP21029=V29', 'SLPGVP21033=V33',
      'SLPGVP21036=V36', 'SLPGVP21038=V38', 'SLPGVP21046=V46', 'SLPGVP21047=V47',
      'SLPGVP21049=V49',
    ],
  },
};

function options(values: readonly string[]): readonly FilterOption[] {
  return values.map((value) => ({ value, label: value }));
}

function lineOptions(packed: readonly string[]): readonly FilterOption[] {
  return packed.map((entry) => {
    const separator = entry.indexOf('=');
    const value = entry.slice(0, separator);
    return { value, label: entry.slice(separator + 1) };
  });
}

/**
 * The filter bar for one factory, on one view. **Call it through `useMemo`** keyed on both: a new
 * array every render would re-parse the URL every render.
 *
 * `floor` is deliberately absent on `summary` — that view is fetched whole-factory and the floor
 * only spotlights lines within a complete result (§4.3), so the spotlight selector owns the same
 * `?floor=` param there and the screen never shows two controls for one value.
 */
export function sewingEfficiencyFilters(
  factory: FactoryCode,
  view: ReportViewName,
): FilterSchema {
  const setup = FACTORY_SETUP[factory];
  return [
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      // Required: there is no "all factories" result, so the control offers no "All" entry.
      required: true,
      defaultValue: factory,
      options: SEWING_FACTORY_OPTIONS,
    },
    {
      type: 'date-range',
      name: 'period',
      label: 'Period',
      required: true,
      fromParam: 'from',
      toParam: 'to',
      defaultValue: defaultPeriod,
    },
    {
      type: 'multi-select',
      name: 'building',
      label: 'Building',
      anyLabel: 'All buildings',
      options: options(setup.buildings),
    },
    // Only where it is a predicate — see the note above.
    ...(view === 'summary'
      ? []
      : ([
          {
            type: 'multi-select',
            name: 'floor',
            label: 'Floor',
            anyLabel: 'All floors',
            options: options(setup.floors),
          },
        ] as const)),
    {
      type: 'multi-select',
      name: 'line',
      label: 'Line',
      anyLabel: 'All lines',
      options: lineOptions(setup.lines),
    },
    {
      type: 'multi-select',
      name: 'brand',
      label: 'Brand',
      anyLabel: 'All brands',
      options: live('brand'),
    },
    {
      // A switch because the server declares this `type: boolean`: it is bound on every call and
      // has no "no choice" state a select's empty option would imply. `false` is legacy's default.
      type: 'switch',
      name: 'include_ppa',
      label: 'PPA lines',
      defaultValue: 'false',
      onLabel: 'Included',
      offLabel: 'Excluded',
    },
  ];
}

/** The report's four governed views (`views:` in `report.yaml`), and the URL param that names one. */
export const VIEW_PARAM = 'view';
const viewSchema = z.enum(['summary', 'daily', 'mo', 'changeover']);
export type ReportViewName = z.infer<typeof viewSchema>;

export function parseView(raw: string | null): ReportViewName {
  const parsed = viewSchema.safeParse(raw);
  // `default_view: summary` in report.yaml — a nonsense view lands on the report, not an error.
  return parsed.success ? parsed.data : 'summary';
}

export const VIEW_TABS: readonly { value: ReportViewName; label: string }[] = [
  { value: 'summary', label: 'By line' },
  { value: 'daily', label: 'By day' },
  { value: 'mo', label: 'By MO' },
  // Last, because it is the only tab that is not a grain of the efficiency fact.
  { value: 'changeover', label: 'Changeover' },
];

/**
 * Which server-computed column family the page shows — `EINS` is what passed end-of-line QC,
 * `SFTS` the sewing last operation.
 *
 * **It is a dataset filter and IS sent to the API.** On `summary` it changes which LINES are in the
 * answer, because the server drops a line with no output, no target and no worked minutes in the
 * selected family — so the table, chart, cards and export stay on one row count (§4.3, §5.2). On
 * the other views `:metric` binds inert.
 */
export const METRIC_PARAM = 'metric';
const metricSchema = z.enum(['EINS', 'SFTS']);
export type Metric = z.infer<typeof metricSchema>;

export function parseMetric(raw: string | null): Metric {
  const parsed = metricSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'EINS';
}

export const METRIC_TABS: readonly { value: Metric; label: string; hint: string }[] = [
  { value: 'EINS', label: 'Endline passed', hint: 'Pieces that passed end-of-line QC' },
  { value: 'SFTS', label: 'Sewing last operation', hint: 'Pieces off the last sewing operation' },
];

/**
 * What the charts plot. Chooses between columns the server already computed and is never sent to
 * the API. `output` is legacy's default and stays the default here.
 */
export const MODE_PARAM = 'mode';
const modeSchema = z.enum(['output', 'efficiency']);
export type ChartMode = z.infer<typeof modeSchema>;

export function parseMode(raw: string | null): ChartMode {
  const parsed = modeSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'output';
}

export const MODE_TABS: readonly { value: ChartMode; label: string }[] = [
  { value: 'output', label: 'Output' },
  { value: 'efficiency', label: 'Efficiency' },
];

/**
 * Chart grain on the summary bar chart only — line (server grain) or Building–Floor rollup.
 * Presentation; never sent to the API. Table and export stay on line grain (CLAUDE.md §4.3,
 * DECISIONS.md §31). Default `line` is today's behaviour.
 */
export const GRAIN_PARAM = 'grain';
const grainSchema = z.enum(['line', 'floor']);
export type ChartGrain = z.infer<typeof grainSchema>;

export function parseGrain(raw: string | null): ChartGrain {
  const parsed = grainSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'line';
}

export const GRAIN_TABS: readonly { value: ChartGrain; label: string }[] = [
  { value: 'line', label: 'Line' },
  { value: 'floor', label: 'Floor' },
];

/** Legacy's auto-load checkbox: these screens are left open on a floor. Off by default. */
export const REFRESH_PARAM = 'refresh';
export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function parseAutoRefresh(raw: string | null): boolean {
  return raw === 'on';
}

/** True when the reader asked for PPA lines to be counted in. Reads the parsed filter value. */
export function includesPpa(values: Record<string, string | null>): boolean {
  return values['include_ppa'] === 'true';
}
